import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { Source } from "@instead/shared";
import { api, ApiRequestError } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";
import { Ic } from "../components/Icons.tsx";
import { QuickAdd } from "../components/QuickAdd.tsx";
import { SourceDefaultsSheet, summarizeDefaults } from "../components/SourceDefaults.tsx";
import { Dialog, Spinner, Toast, useSlow, useToast } from "../components/Ui.tsx";

/**
 * Sources (rounds 2 and 5): the list, an Add that opens the one paste box,
 * Check now per source (floored at a minute), pause, remove with keep/remove
 * items.
 */
export function Sources() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, say] = useToast();
  const [removing, setRemoving] = useState<Source | null>(null);
  const [choice, setChoice] = useState<"keep" | "remove">("keep");
  const [checking, setChecking] = useState<string | null>(null);
  const [editingDefaults, setEditingDefaults] = useState<Source | null>(null);
  const { data } = useQuery({ queryKey: ["sources"], queryFn: api.sources.list });
  // vocabularies behind the one-line defaults summary (D-36)
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const { data: moodsData } = useQuery({ queryKey: ["moods"], queryFn: api.moods.list });
  const { data: needsData } = useQuery({ queryKey: ["needs"], queryFn: api.needs.list });
  const { data: placesData } = useQuery({ queryKey: ["places"], queryFn: api.places.list });
  const vocab = { cats: catData?.categories ?? [], moods: moodsData?.moods ?? [], needs: needsData?.needs ?? [], places: placesData?.places ?? [] };
  const sources = data?.sources ?? [];
  const slow = useSlow(!!checking);

  const refreshAll = async () => {
    await qc.invalidateQueries({ queryKey: ["sources"] });
    await qc.invalidateQueries({ queryKey: ["items"] });
    await qc.invalidateQueries({ queryKey: ["categories"] });
  };
  const toggleM = useMutation({
    mutationFn: (s: Source) => api.sources.update(s.id, { enabled: !s.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });
  const removeM = useMutation({
    mutationFn: (v: { id: string; items: "keep" | "remove" }) => api.sources.remove(v.id, v.items),
    onSuccess: async () => { setRemoving(null); await refreshAll(); },
    onError: (e) => setError(e.message),
  });

  async function checkNow(s: Source) {
    setChecking(s.id);
    setError(null);
    try {
      const { stats } = await api.sources.sync(s.id);
      say(stats.imported > 0 ? t("sources.checked_new", { n: stats.imported }) : t("sources.checked_none"));
      await refreshAll();
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 429) say(t("sources.tooSoon"));
      else setError(e instanceof Error ? e.message : "Check failed.");
    } finally {
      setChecking(null);
    }
  }


  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "");

  return (
    <div>
      <div className="screen-head">
        <button className="icon-btn" aria-label={t("back")} onClick={() => nav({ to: "/settings/shelf" })}>{Ic.back()}</button>
        <h1 className="title">{t("sources.title")}</h1>
        <button className="btn btn-primary" type="button" onClick={() => setAdding(true)}>{Ic.plus({ size: 14 })} {t("sources.add")}</button>
      </div>

      {data && sources.length === 0 && <p className="meta" style={{ textAlign: "center", padding: "var(--sp-6) 0" }}>{t("sources.empty")}</p>}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)", marginTop: "var(--sp-3)" }}>
        {sources.map((s) => (
          <div key={s.id} className="card" style={{ padding: "var(--sp-3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--sp-2)" }}>
              <strong style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</strong>
              <span className="micro">{t(`sources.kind.${s.kind}` as never)}</span>
            </div>
            <p className="meta" style={{ marginTop: 2 }}>
              {t("sources.items", { n: s.itemCount })}
              {s.lastSyncAt ? ` · ${t("sources.checked", { when: when(s.lastSyncAt) })}` : ""}
              {!s.enabled ? ` · ${t("sources.paused")}` : ""}
              {s.lastStatus === "error" ? ` · ${t("sources.failed")}` : ""}
            </p>
            {s.defaults && summarizeDefaults(s.defaults, vocab, t) && (
              <p className="micro" style={{ marginTop: 2, whiteSpace: "normal" }}>{t("sources.defaultsFor", { list: summarizeDefaults(s.defaults, vocab, t) })}</p>
            )}
            <div className="chip-row" style={{ marginTop: "var(--sp-2)" }}>
              <button className="chip" type="button" disabled={checking === s.id || !s.enabled} aria-busy={checking === s.id} onClick={() => checkNow(s)}>
                {checking === s.id && slow ? <Spinner /> : Ic.refresh({ size: 12 })} {t("sources.checkNow")}
              </button>
              <button className="chip" type="button" onClick={() => setEditingDefaults(s)}>{t("sources.defaultsEdit")}</button>
              <button className="chip" type="button" onClick={() => toggleM.mutate(s)}>{s.enabled ? t("sources.pause") : t("sources.resume")}</button>
              <button className="chip" type="button" onClick={() => { setRemoving(s); setChoice("keep"); }}>{t("sources.remove")}</button>
            </div>
          </div>
        ))}
      </div>

      {error && <p role="alert" className="meta" style={{ color: "var(--danger)", marginTop: "var(--sp-2)" }}>{error}</p>}

      {adding && <QuickAdd onClose={() => setAdding(false)} onImported={(n) => say(t("sources.added", { n }))} />}

      {editingDefaults && <SourceDefaultsSheet source={editingDefaults} onClose={() => setEditingDefaults(null)} onSaved={() => say(t("sources.defaultsSaved"))} />}

      {removing && (
        <Dialog title={t("sources.removeQ", { name: removing.label })} onClose={() => setRemoving(null)}>
          <div role="radiogroup" aria-label={t("sources.remove")} style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)", marginTop: "var(--sp-3)" }}>
            <button type="button" role="radio" aria-checked={choice === "keep"} className="picker" style={{ padding: "var(--sp-2) var(--sp-3)", minHeight: 44, border: `1.5px solid ${choice === "keep" ? "var(--accent)" : "var(--line)"}`, background: choice === "keep" ? "var(--accent-soft)" : "transparent" }} onClick={() => setChoice("keep")}>
              <span className={`tick ${choice === "keep" ? "on" : ""}`} style={{ width: 18, height: 18 }}>{choice === "keep" && Ic.check({ size: 12 })}</span>
              <span className="grow" style={{ fontWeight: 600, color: "var(--accent-ink)" }}>{t("sources.keepItems", { n: removing.itemCount })}</span>
            </button>
            <button type="button" role="radio" aria-checked={choice === "remove"} className="picker" style={{ padding: "var(--sp-2) var(--sp-3)", minHeight: 44, border: `1.5px solid ${choice === "remove" ? "var(--danger)" : "var(--line)"}`, background: "transparent" }} onClick={() => setChoice("remove")}>
              <span className="tick" style={{ width: 18, height: 18, borderColor: "var(--danger)", background: choice === "remove" ? "var(--danger)" : "transparent", color: "var(--on-accent)" }}>{choice === "remove" && Ic.check({ size: 12 })}</span>
              <span className="grow" style={{ fontWeight: 600, color: "var(--danger)" }}>{t("sources.removeItems", { n: removing.itemCount })}</span>
            </button>
          </div>
          <div className="dialog-actions">
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{t("cancel")}</button>
            <button className={`btn ${choice === "remove" ? "btn-danger" : "btn-primary"}`} type="button" disabled={removeM.isPending} onClick={() => removeM.mutate({ id: removing.id, items: choice })}>{t("remove")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </div>
  );
}
